import React, { forwardRef, ReactNode } from "react";
import Button from "../Button/Button";
import styles from "./FormCard.module.css";
import { mergeClassNames } from "../../design-tokens";

export interface FormCardProps {
  /** Título do card (obrigatório) */
  title: string;
  /** Subtítulo ou texto descritivo abaixo do título (opcional) */
  description?: string;
  /** Ícone exibido no header (opcional) */
  icon?: string;
  /** Texto descritivo que aparecerá se o usuário passar o mouse no ícone de informação */
  infoText?: string;
  /** Ícone de informação (opcional) */
  infoIcon?: string;
  /** Texto descritivo ao final do formulário (opcional) */
  footerText?: string;
  /** Ativa/desativa o preenchimento e envio do formulário */
  disabled?: boolean;
  /** Função que vai executar quando o formulário for submetido */
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Função executada ao clicar no botão de cancelar/voltar */
  onCancel?: () => void;
  /** Número de colunas para organização dos inputs */
  numColumns: number;
  /** Label do botão de enviar formulário */
  submitButtonLabel?: string;
  /** Label do botão de cancelar/voltar do formulário (opcional) */
  cancelButtonLabel?: string;
  /** Ícone do botão de submissão (opcional) */
  submitButtonIcon?: string;
  /** Desabilita apenas o botão de submissão */
  submitDisabled?: boolean;
  /** Indica estado de carregamento no botão de submissão */
  loading?: boolean;
  /** Componentes internos do formulário (inputs) */
  children?: ReactNode;
  /** Classes adicionais do container */
  className?: string;
}

/**
 * Componente padrão para formulários no sistema.
 * Agrega os inputs organizados em um grid de colunas e fornece header e footer padronizados.
 */
export const FormCard = forwardRef<HTMLDivElement, FormCardProps>(
  (
    {
      title,
      description,
      icon,
      infoText,
      infoIcon,
      footerText,
      disabled = false,
      onSubmit,
      onCancel,
      numColumns,
      submitButtonLabel,
      cancelButtonLabel,
      submitButtonIcon,
      submitDisabled = false,
      loading = false,
      children,
      className,
    },
    ref
  ) => {
    // Mesclar classes com className opcional
    const containerClasses = mergeClassNames(styles.container, className);

    // Evitar o recarregamento padrão da página caso a função passada pelo usuário não o faça
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      if (!disabled && onSubmit) {
        onSubmit(e);
      }
    };

    return (
      <div ref={ref} className={containerClasses}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {icon && (
              <span className={mergeClassNames("material-symbols-outlined", styles.icon)} aria-hidden="true">
                {icon}
              </span>
            )}
            <div className={styles.headerTitles}>
              <h3 className={styles.title}>{title}</h3>
              {description && <p className={styles.description}>{description}</p>}
            </div>
          </div>
          
          {infoIcon && (
            <span
              className={mergeClassNames("material-symbols-outlined", styles.infoIcon)}
              title={infoText}
              aria-label={infoText || "Informação sobre o formulário"}
              role="tooltip"
            >
              {infoIcon}
            </span>
          )}
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className={styles.form}>
          <fieldset disabled={disabled} className={mergeClassNames(styles.fieldset, disabled && styles.disabled)}>
            
            {/* Input Grid */}
            <div 
              className={styles.content} 
              style={{ gridTemplateColumns: `repeat(${numColumns}, minmax(0, 1fr))` }}
            >
              {children}
            </div>

            {/* Footer Text */}
            {footerText && (
              <div className={styles.footerText}>
                {footerText}
              </div>
            )}
          </fieldset>

          {/*
            Actions (Footer)
            Ficam fora do <fieldset> de propósito: um fieldset desabilitado desabilita todos os
            controles descendentes, o que atingiria também o botão de cancelar/voltar — que é
            navegação, não edição. O botão de submissão continua desabilitado explicitamente.
          */}
          {(submitButtonLabel || cancelButtonLabel) && (
            <div className={styles.actions}>
              {cancelButtonLabel && (
                <Button
                  type="button"
                  label={cancelButtonLabel}
                  variant="outlined"
                  severity="secondary"
                  onClick={onCancel}
                />
              )}
              {submitButtonLabel && (
                <Button
                  type="submit"
                  label={submitButtonLabel}
                  variant="filled"
                  severity="primary"
                  disabled={disabled || submitDisabled}
                  loading={loading}
                  icon={submitButtonIcon}
                />
              )}
            </div>
          )}
        </form>
      </div>
    );
  }
);

FormCard.displayName = "FormCard";

export default FormCard;
